import { MarketplaceItemConfig } from "@/services/marketplace/item/marketplaceItemConfig";
import type { MarketplaceItem_ShippingSetup } from "@/services/marketplace/item/marketplaceItemTypes";
import type { Price, PriceNumeric } from "@/services/prices/priceTypes";
import { isNumericPrice } from "@/services/prices/priceValidators";
import { gettext } from "@/services/translation";

export abstract class MarketplaceItemDataNotValidException extends Error {}

export class MarketplaceItemPriceNotValidException extends MarketplaceItemDataNotValidException {}

export function validatePrice(price: Price | undefined): price is PriceNumeric {
  if (!isNumericPrice(price)) {
    throw new MarketplaceItemPriceNotValidException(gettext.$gettext("Cena musí být vyplněna."));
  }

  const amountCents = price.price * 100;

  if (amountCents < MarketplaceItemConfig.priceCentsMin) {
    const msg = gettext.$gettext("Cena musí být větší než %{min}.", { min: MarketplaceItemConfig.priceCentsMin / 100 });
    throw new MarketplaceItemPriceNotValidException(msg);
  }

  if (amountCents > MarketplaceItemConfig.priceCentsMax) {
    const msg = gettext.$gettext("Cena nesmí být větší než %{max}.", { max: MarketplaceItemConfig.priceCentsMax / 100 });
    throw new MarketplaceItemPriceNotValidException(msg);
  }

  return true;
}

export class MarketplaceItemQuantityNotValidException extends MarketplaceItemDataNotValidException {}

export function validateQuantity(quantity: number | undefined): quantity is number {
  if (quantity === undefined) {
    throw new MarketplaceItemQuantityNotValidException(gettext.$gettext("Počet kusů musí být vyplněn."));
  }

  if (typeof quantity !== "number") {
    throw new MarketplaceItemQuantityNotValidException(gettext.$gettext("Nevalidní hodnota."));
  }

  if (quantity < MarketplaceItemConfig.quantityMin) {
    const msg = gettext.$gettext("Počet kusů musí být větší než %{min}.", { min: MarketplaceItemConfig.quantityMin });
    throw new MarketplaceItemQuantityNotValidException(msg);
  }

  if (quantity > MarketplaceItemConfig.quantityMax) {
    const msg = gettext.$gettext("Počet kusů nesmí být větší než %{max}.", { max: MarketplaceItemConfig.quantityMax });
    throw new MarketplaceItemQuantityNotValidException(msg);
  }

  return true;
}

export class MarketplaceItemNameNotValidException extends MarketplaceItemDataNotValidException {}

export function validateName(name: string | undefined): name is string {
  if (name === undefined || name === "") {
    throw new MarketplaceItemNameNotValidException(gettext.$gettext("Název je povinná položka."));
  }

  if (typeof name !== "string") {
    throw new MarketplaceItemNameNotValidException(gettext.$gettext("Nevalidní hodnota."));
  }

  if (name.length < MarketplaceItemConfig.nameMinLength) {
    const msg = gettext.$gettext("Název musí být alespoň %{min} znaků dlouhý.", { min: MarketplaceItemConfig.nameMinLength });
    throw new MarketplaceItemNameNotValidException(msg);
  }

  if (name.length > MarketplaceItemConfig.nameMaxLength) {
    const msg = gettext.$gettext("Název nesmí být delší než %{max} znaků.", { max: MarketplaceItemConfig.nameMaxLength });
    throw new MarketplaceItemNameNotValidException(msg);
  }

  return true;
}

export class MarketplaceItemDescriptionNotValidException extends MarketplaceItemDataNotValidException {}

export function validateDescription(description: string | undefined): boolean {
  if (description === undefined || description === "") {
    return true;
  }

  if (typeof description !== "string") {
    throw new MarketplaceItemDescriptionNotValidException(gettext.$gettext("Nevalidní hodnota."));
  }

  if (description.length < MarketplaceItemConfig.descriptionMinLength) {
    const msg = gettext.$gettext("Popis musí být alespoň %{min} znaků dlouhý.", { min: MarketplaceItemConfig.descriptionMinLength });
    throw new MarketplaceItemDescriptionNotValidException(msg);
  }

  if (description.length > MarketplaceItemConfig.descriptionMaxLength) {
    const msg = gettext.$gettext("Popis nesmí být delší než %{max} znaků.", { max: MarketplaceItemConfig.descriptionMaxLength });
    throw new MarketplaceItemDescriptionNotValidException(msg);
  }

  return true;
}

export class MarketplaceItemShippingSetupNotValidException extends MarketplaceItemDataNotValidException {}

export function validateShippingSetup(setup: MarketplaceItem_ShippingSetup): void {
  if (setup.size === 0) {
    throw new MarketplaceItemShippingSetupNotValidException(gettext.$gettext("Je nutno vybrat alespoň jednu dopravu."));
  }
}
